import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { Accordion, useAccordionToggle } from 'react-bootstrap';
import { connect } from 'react-redux';
import { getIcon, RequestBuilderPage } from '../RequestBuilder/utils';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISelectedSurvey } from '../../../../common/models/survey/ISelectedSurvey';
import { ISelectedPosition } from '../../../../common/models/position/ISelectedPosition';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import { IRefReqCounters } from '../../../../common/models/referenceRequest/IRefReqCounters';
import Table from '../../../../components/Table';
import { getSelectedContactsColumns, isWarningShown } from '../Step2/utils';
import styles from './styles.module.scss';
import LinkButton from '../../../../components/LinkButton';
import { ReactComponent as PencilIcon } from '../../../../assets/icons/pencil.svg';
import { ReactComponent as ChevronUp } from '../../../../assets/icons/chevron-up.svg';
import { pipe, slice, map, join } from 'ramda';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import { getStep3Text } from '../../../../common/helpers/refCountersHelper';

interface IProps {
  step: RequestBuilderPage;
  order: number;
  references: INewContactInfo[] | null;
  selectedPosition: ISelectedPosition | null;
  selectedSurvey: ISelectedSurvey | null;
  candidates: IRefReqSelectedContact[];
  refCounters: IRefReqCounters;
  isAdvancedRequest: boolean;
  intl: IntlShape;
  onEdit: IBindingCallback1<RequestBuilderPage>;
}

const ReviewItem: FunctionComponent<IProps> = ({
  isAdvancedRequest,
  selectedPosition,
  selectedSurvey,
  refCounters,
  references,
  candidates,
  onEdit,
  step,
  intl
}) => {
  const [isShown, setIsShown] = useState(false);

  const getCountersText = () => {
    const countersText = !references && isAdvancedRequest && getStep3Text(refCounters, intl);

    return (
      <>
        {references ? references.length : refCounters.totalCount}
        &nbsp;
        <span className="text-lowercase">
          <FormattedMessage id="card.references.title" defaultMessage="References" />
          {countersText && (
            <>
              &nbsp;
              <FormattedMessage
                id="review.atLeast"
                defaultMessage="(at least {text})"
                values={{ text: countersText }}
              />
            </>
          )}
        </span>
      </>
    );
  };

  const getCandidatesText = () => {
    const text = pipe<IRefReqSelectedContact[], IRefReqSelectedContact[], string[], string>(
      slice(0, 3),
      map(({ firstName, lastName }) => `${firstName} ${lastName}`),
      join(', ')
    )(candidates);

    return candidates.length > 3
      ? (
        <>
          {text}
          <span className={`${styles.badge} number-badge`}>{`+${candidates.length - 3}`}</span>
        </>
      ) : text;
  };

  const getText = () => {
    switch (step) {
      case RequestBuilderPage.Step1:
        return selectedPosition?.name || (
          <span className="secondary-text"><FormattedMessage id="none" defaultMessage="None" /></span>
        );
      case RequestBuilderPage.Step2:
        return getCandidatesText();
      case RequestBuilderPage.Step3:
        return getCountersText();
      case RequestBuilderPage.Step4:
        return selectedSurvey?.name;
      default:
        return null;
    }
  };

  const getItemContent = (onToggle?: IBindingCallback1<SyntheticEvent>) => (
    <div className="d-flex align-items-center">
      <div className="main-icon">
        {getIcon(step)}
      </div>
      <div className={styles.titleContainer}>
        <div className={styles.title}><FormattedMessage defaultMessage={step} id={`card.${step}.title`} /></div>
        <div className={styles.titleInfo}>
          {getText()}
          {onToggle && (
            <LinkButton
              onClick={onToggle}
              textIntlId={isShown ? 'collapse' : 'showAll'}
              defaultMessage={isShown ? 'Collapse' : 'Show all'}
              icon={(
                <ChevronUp
                  className={styles.arrowIcon}
                  style={isShown ? undefined : { transform: 'rotate(-180deg)' }}
                />
              )}
              iconPosition="right"
            />
          )}
        </div>
      </div>
      <div className={styles.editIcon} role="presentation" onClick={() => onEdit(step)}>
        <LinkButton
          icon={<PencilIcon />}
          textIntlId="review.edit"
          defaultMessage="Edit {value}"
          onClick={() => onEdit(step)}
          intlValues={{ value: (
            <span className="text-lowercase">
              &nbsp;
              <FormattedMessage id={step} defaultMessage={step} />
            </span>
          ) }}
        />
      </div>
    </div>
  );

  const Toggler = ({ eventKey }: { eventKey: string }) => {
    const onToggle = useAccordionToggle(eventKey, () => setIsShown(!isShown));

    return (
      <div className={styles.togglerContainer}>
        {getItemContent(onToggle)}
      </div>
    );
  };

  return (
    <div className={`toggler-container ${(step === RequestBuilderPage.Step2 && isShown) ? styles.step2Item : ''}`}>
      {step === RequestBuilderPage.Step2 ? (
        <Accordion>
          <Toggler eventKey="0" />
          <Accordion.Collapse eventKey="0">
            <div className="mt-4" style={{ height: `calc(${candidates.length * 65}px + 3rem` }}>
              <Table
                config={{
                  data: { items: candidates, total: candidates.length },
                  columns: getSelectedContactsColumns({
                    withWarning: isWarningShown(candidates),
                    position: selectedPosition?.name as string
                  })
                }}
              />
            </div>
          </Accordion.Collapse>
        </Accordion>
      ) : getItemContent()}
    </div>
  );
};

const mapStateToProps = ({
  requestBuilder: { selectedPosition, selectedSurvey, candidates, refCounters, isAdvancedRequest, references }
}: IAppState) => ({
  candidates,
  refCounters,
  selectedSurvey,
  selectedPosition,
  isAdvancedRequest,
  references
});

export default connect(mapStateToProps)(injectIntl(ReviewItem));
